/**
 * @file index.tsx
 *
 * @fileoverview Renders a hero row with a centered search banner.
 */
import React from 'react';
import { HeroSearchBannerCenteredParagraph } from '~/jobiqo/types/types';
import Image from '~/jobiqo/components/Image';
import { JobSearchForm } from '~/components/Form/SearchInputForm';
import { WYSIWYGText } from '~/vcl/atoms/13-text/02-WYSIWYG';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import styles from './heroSearchBannerCentered.module.scss';
import { Main } from '~/vcl/atoms/06-layout/09-main';

interface HeroSearchBannerCenteredProps {
  /**
   * The row to display.
   */
  row: HeroSearchBannerCenteredParagraph;
  /**
   * The index of this row. Used to detect if its the first row on the page.
   */
  index: number;
  /**
   * The max container size.
   */
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}

export const HeroSearchBannerCentered = ({
  row,
  index,
  maxContainerSize,
}: HeroSearchBannerCenteredProps) => {
  const overlayTransparency = row.overlayTransparency ?? 100;
  // We need to convert the transparency value to opacity in order to use it in the CSS.
  const overlayOpacity = 1 - overlayTransparency / 100;

  return (
    <div className={`${styles.wrapper} jd-hero-search-centered-wrapper`}>
      <div
        className={`${styles.overlay} jd-hero-search-centered-overlay`}
        style={{ '--overlay-opacity': overlayOpacity } as React.CSSProperties}
      >
        <div
          className={`${styles.background} jd-hero-search-centered-background`}
        >
          <Image
            src={row.image?.url}
            width={null}
            height={null}
            priority={index === 0}
            alt={row.image?.alt ?? ''}
            {...(row.image.title && { title: row.image.title })}
            {...(row.image.caption && { caption: row.image.caption })}
            quality={100}
          />
        </div>
      </div>
      <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
        <div className={`${styles.text} jd-hero-search-centered-text`}>
          <Spacing
            paddingTop={[5, row.spacing ?? 10]}
            paddingBottom={[0, row.spacing ?? 1]}
          >
            {row.text && <WYSIWYGText format="full_html" content={row.text} />}
          </Spacing>
        </div>
        <JobSearchForm />
      </Main>
    </div>
  );
};
export default HeroSearchBannerCentered;
